import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import api, { errMsg } from "../api";
import { useAuth } from "../auth";

const LIFT_NAMES = { squat: "Squat", bench: "Bench Press", row: "Barbell Row", ohp: "Overhead Press", deadlift: "Deadlift" };

// 'A'/'B' render as "Workout A/B"; v6 program sessions store their label text
// ("Day A") in workout_type and render as-is.
const workoutLabel = (t) => (t === "A" || t === "B" ? `Workout ${t}` : t);

/* Assign (or clear) the student's custom program. Any change resets their
 * rotation pointer to the first workout, so it always asks before applying. */
function ProgramAssign({ studentId, programId, programName, onChange }) {
  const [programs, setPrograms] = useState(null);
  const [sel, setSel] = useState(programId == null ? "" : String(programId));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/programs")
      .then(({ data }) => setPrograms(data.filter((p) => p.active)))
      .catch((e) => setErr(errMsg(e)));
  }, []);
  useEffect(() => { setSel(programId == null ? "" : String(programId)); }, [programId]);

  const dirty = sel !== (programId == null ? "" : String(programId));
  const apply = async () => {
    const target = sel === "" ? null : Number(sel);
    const targetName = target === null ? "the standard A/B rotation" : `"${(programs.find((p) => p.id === target) || {}).name}"`;
    if (!window.confirm(`Move this student to ${targetName}? Their rotation restarts at the first workout.`)) return;
    setBusy(true); setErr("");
    try { await api.put(`/admin/users/${studentId}/program`, { program_id: target }); onChange(); }
    catch (e) { setErr(errMsg(e, "Could not change the program")); }
    finally { setBusy(false); }
  };

  return (
    <div className="llh-card llh-mb-12">
      <div className="llh-eyebrow llh-mb-8">Training program</div>
      <div className="llh-as-current">
        Currently on <strong className="llh-strong-text">{programName || "the standard A/B rotation"}</strong>.
      </div>
      {programs === null && !err ? <div className="llh-spin" /> : (
        <div className="llh-row-wrap">
          <select value={sel} onChange={(e) => setSel(e.target.value)} className="llh-select">
            <option value="">Standard A/B (Ironlifts 5x5)</option>
            {(programs || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="llh-btn-primary llh-btn--dim" disabled={busy || !dirty} onClick={apply}>
            {busy ? "Applying..." : "Apply"}
          </button>
        </div>
      )}
      {err && <div className="llh-error-text llh-mt-8">{err}</div>}
      <div className="llh-help-text">
        Assigning a program replaces the student's A/B rotation with its workouts.
        Their logged history and working weights carry over either way. Build and
        edit programs on the <Link to="/admin/programs" className="llh-c-brass">Programs</Link> page.
      </div>
    </div>
  );
}

function CommentForm({ sessionId, onChange }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const post = async () => {
    if (!body.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/sessions/${sessionId}/comment`, { body: body.trim() });
      onChange(); // reload; this form unmounts once the comment exists
    } catch (e) { setErr(errMsg(e, "Could not post comment")); setBusy(false); }
  };
  return (
    <div className="llh-sep">
      <textarea className="llh-textarea" value={body} placeholder="Leave feedback on this session..." onChange={(e) => setBody(e.target.value)} />
      {err && <div className="llh-error-text llh-mb-8">{err}</div>}
      <button className="llh-btn-primary llh-btn--dim" disabled={busy || !body.trim()} onClick={post}>
        {busy ? "Posting..." : "Post comment"}
      </button>
    </div>
  );
}

function SessionCard({ s, meId, liftNames, onChange }) {
  const byLift = {};
  (s.sets || []).forEach((st) => {
    if (!byLift[st.exercise]) byLift[st.exercise] = { reps: [], weight: Number(st.weight_lb) };
    byLift[st.exercise].reps.push(st.reps);
  });
  const name = (k) => (liftNames && liftNames[k]) || LIFT_NAMES[k] || k;
  const comments = s.comments || [];
  const iCommented = comments.some((c) => Number(c.coach_id) === Number(meId));

  const toggleHide = async (c) => {
    try { await api.put(`/admin/comments/${c.id}/hide`, { hidden: !c.hidden }); onChange(); }
    catch (e) { alert(errMsg(e)); }
  };

  return (
    <div className="llh-card llh-mb-12">
      <div className="llh-session-head">
        <span className="llh-session-title">{workoutLabel(s.workout_type)}</span>
        <span className="llh-dim-12">{String(s.workout_date).slice(0, 10)}</span>
      </div>
      {Object.keys(byLift).map((k) => (
        <div key={k} className="llh-lift-row">
          <span>{name(k)}</span>
          <span className="llh-tnum">{byLift[k].weight} lb · {byLift[k].reps.join("/")}</span>
        </div>
      ))}
      {s.notes && <div className="llh-session-notes">{s.notes}</div>}

      {comments.map((c) => {
        const mine = Number(c.coach_id) === Number(meId);
        return (
          <div key={c.id} className={c.hidden ? "llh-comment llh-comment--hidden" : "llh-comment"}>
            <div className="llh-comment-head">
              <span className={c.hidden ? "llh-comment-author llh-dim" : "llh-comment-author"}>
                Coach {c.coach}{mine ? " (you)" : ""}{c.hidden ? " · hidden" : ""}
              </span>
              {mine && <button className="llh-btn-tiny" onClick={() => toggleHide(c)}>{c.hidden ? "Unhide" : "Hide from student"}</button>}
            </div>
            <div className="llh-comment-body">{c.body}</div>
          </div>
        );
      })}

      {!iCommented && <CommentForm sessionId={s.id} onChange={onChange} />}
    </div>
  );
}

export default function AdminStudent() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [liftNames, setLiftNames] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setErr("");
    api.get(`/admin/students/${id}`).then(({ data }) => setData(data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Lift display names for session cards; raw keys are the fallback.
    api.get("/lifts").then(({ data }) => {
      const m = {};
      data.forEach((l) => { m[l.key] = l.name; });
      setLiftNames(m);
    }).catch(() => setLiftNames({}));
  }, []);

  const sessions = data && data.sessions ? data.sessions : [];

  return (
    <div className="llh-page llh-pb-40">
      <header className="llh-header-bar llh-narrow">
        <Link to="/admin/overview" className="llh-navlink"><span className="llh-nav-caret">‹</span> Overview</Link>
        <div className="llh-header-title">STUDENT</div>
        <Link to="/admin" className="llh-navlink">Admin</Link>
      </header>

      <main className="llh-main llh-narrow">
        {err && <div className="llh-error-text llh-mb-12">{err}</div>}
        {data === null && !err ? <div className="llh-spin" /> : data && (
          <>
            <div className="llh-eyebrow">Student</div>
            <h2 className="llh-h2 llh-mb-16">{data.display_name}</h2>

            <ProgramAssign studentId={id} programId={data.program_id} programName={data.program_name} onChange={load} />

            {!data.share_progress ? (
              <div className="llh-card llh-mb-12 llh-card--message">This student hasn't shared their training.</div>
            ) : sessions.length === 0 ? (
              <div className="llh-card llh-mb-12 llh-card--message">No sessions logged yet.</div>
            ) : (
              <>
                {!data.share_notes && (
                  <p className="llh-share-note">
                    This student shares their training but keeps their own session notes private. You can still leave feedback.
                  </p>
                )}
                {sessions.map((s) => <SessionCard key={s.id} s={s} meId={user.id} liftNames={liftNames} onChange={load} />)}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
